(() => {
  if (customElements.get('impressiko-fbt')) return;
  const preservedSelections = new Map();

  class ImpressikoFBT extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        this.bindVariantSync();
        return;
      }
      this.dataset.initialized = 'true';
      this.init();
    }

    disconnectedCallback() {
      if (this.state) preservedSelections.set(this.id, this.state.map((item) => ({
        product: { id: item.product.id },
        selected: item.selected,
        variantId: item.variantId,
      })));
      this.abortControllers?.forEach((controller) => controller.abort());
      this.abortControllers?.clear();
      if (this.productInfo && this.onVariantChange) {
        this.productInfo.removeEventListener('change', this.onVariantChange, true);
        this.productInfo.removeEventListener('variant:change', this.onVariantChange);
      }
      this.variantSyncBound = false;
    }

    bindVariantSync() {
      if (this.variantSyncBound) return;
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) return;
      this.onVariantChange ||= () => {
        window.requestAnimationFrame(() => this.syncCurrentVariant());
      };
      this.productInfo.addEventListener('change', this.onVariantChange, true);
      this.productInfo.addEventListener('variant:change', this.onVariantChange);
      this.variantSyncBound = true;
    }

    syncCurrentVariant() {
      const formId = this.dataset.productFormId;
      const form = (formId && document.getElementById(formId)) ||
        this.productInfo?.querySelector('product-form form, form[action*="/cart/add"]');
      const variantId = Number(form?.querySelector('input[name="id"]')?.value || 0);
      if (!variantId || variantId === this.currentVariantId) return;
      this.currentVariantId = variantId;
      const currentItem = this.state?.[0];
      if (!currentItem) return;
      if (!currentItem.product.variants.some((variant) => variant.id === variantId)) return;
      currentItem.variantId = variantId;
      const selector = this.querySelector('[data-fbt-row-index="0"] .impressiko-fbt__variant-select');
      if (selector) selector.value = String(variantId);
      this.update();
    }

    async init() {
      this.loadingElement = this.querySelector('[data-fbt-loading]');
      this.contentElement = this.querySelector('[data-fbt-content]');
      this.visualsElement = this.querySelector('[data-fbt-visuals]');
      this.rowsElement = this.querySelector('[data-fbt-rows]');
      this.totalElement = this.querySelector('[data-fbt-total]');
      this.compareElement = this.querySelector('[data-fbt-compare]');
      this.buttonElement = this.querySelector('[data-fbt-add]');
      this.messageElement = this.querySelector('[data-fbt-message]');
      this.currentProductId = Number(this.dataset.currentProductId || 0);
      this.currentVariantId = Number(this.dataset.currentVariantId || 0);
      this.showCompare = this.dataset.showCompare === 'true';
      this.showVariants = this.dataset.showVariants === 'true';
      this.showTitles = this.dataset.showTitles === 'true';
      this.currency = this.dataset.currency || 'INR';
      this.locale = this.dataset.locale || 'en-IN';
      this.abortControllers = new Set();
      this.buttonElement.addEventListener('click', () => this.addSelectedToCart());
      this.bindVariantSync();

      try {
        const productScript = this.querySelector('[data-fbt-products]');
        if (!productScript) throw new Error('Bundle product data is unavailable.');

        const productEntries = JSON.parse(productScript.textContent);
        const immediateProducts = productEntries
          .map((entry) => this.normalizeProduct(entry.product, entry.source))
          .filter(Boolean);

        if (!immediateProducts.length) throw new Error('No products are available for this bundle.');

        this.setProducts(immediateProducts);
        this.showContent();

        const hydratedProducts = await this.hydrateProductEntries(productEntries);
        const manualProducts = this.uniqueProducts(hydratedProducts).slice(0, 3);
        if (manualProducts.length) this.setProducts(manualProducts);

        if (this.dataset.autoFill === 'true' && this.products.length < 3) {
          const automaticProducts = await this.fetchAutomaticProducts();
          const completedProducts = this.uniqueProducts([
            ...this.products,
            ...automaticProducts,
          ]).slice(0, 3);
          if (completedProducts.length > this.products.length) this.setProducts(completedProducts);
        }
      } catch (error) {
        this.contentElement.hidden = true;
        this.loadingElement.innerHTML = '';
        this.loadingElement.textContent = error.message || 'Unable to load product recommendations.';
        this.loadingElement.style.color = 'var(--ifbt-error-color)';
        this.loadingElement.hidden = false;
      }
    }

    showContent() {
      this.loadingElement.hidden = true;
      this.contentElement.hidden = false;
    }

    setProducts(products) {
      const existingState = this.state || preservedSelections.get(this.id) || [];
      const previousState = new Map(
        existingState.map((item) => [item.product.id, item])
      );

      this.products = this.uniqueProducts(products).slice(0, 3);
      this.state = this.products.map((product, index) => {
        const previousItem = previousState.get(product.id);
        const previousVariant = previousItem
          ? product.variants.find((variant) => variant.id === previousItem.variantId)
          : null;

        return {
          product,
          selected: previousItem
            ? previousItem.selected
            : this.dataset.allSelected === 'true',
          variantId: index === 0 && !this.state
            ? this.initialVariantId(product, index)
            : previousVariant?.available
              ? previousVariant.id
              : this.initialVariantId(product, index),
        };
      });

      this.render();
    }

    async hydrateProductEntries(entries) {
      const products = await Promise.all(
        entries.map(async (entry) => {
          const embeddedProduct = this.normalizeProduct(entry.product, entry.source);
          if (embeddedProduct) return embeddedProduct;
          if (!entry.handle) return null;

          try {
            const product = await this.fetchJson(
              `${this.routesRoot()}products/${encodeURIComponent(entry.handle)}.js`
            );
            return this.normalizeProduct(product, entry.source);
          } catch (error) {
            return null;
          }
        })
      );

      return products.filter(Boolean);
    }

    async fetchJson(url, timeout = 4500) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeout);
      this.abortControllers.add(controller);

      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Request failed with status ${response.status}.`);
        return await response.json();
      } finally {
        window.clearTimeout(timeoutId);
        this.abortControllers.delete(controller);
      }
    }

    normalizeProduct(product, source = 'automatic') {
      if (!product || !product.id || !Array.isArray(product.variants)) return null;

      const fallbackImage = this.imageUrl(
        product.featured_image || (Array.isArray(product.images) ? product.images[0] : null)
      );

      const variants = product.variants.map((variant) => ({
        id: Number(variant.id),
        title: variant.title || 'Default',
        price: this.toCents(variant.price),
        compareAtPrice: this.toCents(variant.compare_at_price),
        available: variant.available !== false,
        image: this.imageUrl(variant.featured_image) || fallbackImage,
      }));

      return {
        id: Number(product.id),
        title: product.title || '',
        handle: product.handle || '',
        url: product.url || (product.handle ? `${this.routesRoot()}products/${product.handle}` : '#'),
        image: fallbackImage,
        source,
        variants,
      };
    }

    uniqueProducts(products) {
      const seen = new Set();
      return products.filter((product) => {
        if (!product || seen.has(product.id)) return false;
        seen.add(product.id);
        return true;
      });
    }

    async fetchAutomaticProducts() {
      const limit = Math.max(3, Number(this.dataset.recommendationLimit || 8));
      let candidates = [];

      try {
        const recommendationsUrl = `${this.routesRoot()}recommendations/products.json?product_id=${this.currentProductId}&limit=${limit}&intent=related`;
        const data = await this.fetchJson(recommendationsUrl);
        candidates = (data.products || [])
          .map((product) => this.normalizeProduct(product, 'automatic'))
          .filter(Boolean);
      } catch (error) {
        candidates = [];
      }

      if (this.dataset.randomize === 'true') this.shuffle(candidates);

      const existingIds = new Set(this.products.map((product) => product.id));
      candidates = candidates.filter(
        (product) => !existingIds.has(product.id) && product.variants.some((variant) => variant.available)
      );

      if (candidates.length + this.products.length < 3) {
        try {
          const fallbackData = await this.fetchJson(
            `${this.routesRoot()}collections/all/products.json?limit=24`
          );
          let fallbackProducts = (fallbackData.products || [])
            .map((product) => this.normalizeProduct(product, 'automatic'))
            .filter(Boolean);
          if (this.dataset.randomize === 'true') this.shuffle(fallbackProducts);
          const usedIds = new Set([...existingIds, ...candidates.map((product) => product.id)]);
          candidates.push(
            ...fallbackProducts.filter(
              (product) =>
                !usedIds.has(product.id) && product.variants.some((variant) => variant.available)
            )
          );
        } catch (error) {
          // Related recommendations remain usable even if the optional fallback is unavailable.
        }
      }

      return candidates;
    }

    shuffle(items) {
      for (let index = items.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
      }
    }

    routesRoot() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    toCents(value) {
      if (value === null || value === undefined || value === '') return 0;
      if (typeof value === 'string' && value.includes('.')) {
        const decimalValue = Number.parseFloat(value);
        return Number.isFinite(decimalValue) ? Math.round(decimalValue * 100) : 0;
      }
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return 0;
      return Math.round(numericValue);
    }

    imageUrl(value) {
      if (!value) return '';
      let url = '';
      if (typeof value === 'string') url = value;
      else url = value.src || value.url || value.preview_image?.src || '';
      if (url.startsWith('//')) return `https:${url}`;
      return url;
    }

    initialVariantId(product, index) {
      if (index === 0 && this.currentVariantId) {
        const requested = product.variants.find((variant) => variant.id === this.currentVariantId);
        if (requested) return requested.id;
      }
      return (
        product.variants.find((variant) => variant.available)?.id || product.variants[0]?.id || 0
      );
    }

    render() {
      this.visualsElement.innerHTML = '';
      this.rowsElement.innerHTML = '';

      this.state.forEach((item, index) => {
        if (index > 0) {
          const plus = document.createElement('span');
          plus.className = 'impressiko-fbt__plus';
          plus.textContent = '+';
          plus.setAttribute('aria-hidden', 'true');
          this.visualsElement.appendChild(plus);
        }

        this.visualsElement.appendChild(this.createVisual(item, index));
        this.rowsElement.appendChild(this.createRow(item, index));
      });

      this.update();
    }

    createVisual(item, index) {
      const visual = document.createElement('div');
      visual.className = 'impressiko-fbt__visual';
      visual.dataset.fbtVisualIndex = String(index);

      const variant = this.getVariant(item);
      this.setVisualImage(visual, variant?.image || item.product.image, item.product.title);
      return visual;
    }

    setVisualImage(container, source, title) {
      container.innerHTML = '';
      if (source) {
        const image = document.createElement('img');
        image.src = source;
        image.alt = title;
        image.loading = 'lazy';
        image.width = 240;
        image.height = 240;
        container.appendChild(image);
      } else {
        container.innerHTML = `<svg class="impressiko-fbt__visual-placeholder" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13Z" stroke="currentColor" stroke-width="1.5"/><path d="m5 17 4.2-4.2a1.2 1.2 0 0 1 1.7 0l1.7 1.7 1.5-1.5a1.2 1.2 0 0 1 1.7 0L20 17.2" stroke="currentColor" stroke-width="1.5"/><circle cx="15.5" cy="8.5" r="1.5" fill="currentColor"/></svg>`;
      }
    }

    createRow(item, index) {
      const row = document.createElement('div');
      row.className = 'impressiko-fbt__row';
      row.dataset.fbtRowIndex = String(index);

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'impressiko-fbt__checkbox';
      checkbox.checked = item.selected;
      checkbox.setAttribute('aria-label', `Select ${item.product.title}`);
      checkbox.addEventListener('change', () => {
        item.selected = checkbox.checked;
        this.update();
      });
      row.appendChild(checkbox);

      const titleElement = index === 0 ? document.createElement('span') : document.createElement('a');
      titleElement.className =
        index === 0 ? 'impressiko-fbt__product-title' : 'impressiko-fbt__product-link';
      if (index > 0) titleElement.href = item.product.url;
      const prefix = index === 0 && this.dataset.currentLabel ? `${this.dataset.currentLabel} ` : '';
      titleElement.textContent = this.showTitles ? `${prefix}${item.product.title}` : prefix.trim();
      if (!titleElement.textContent) titleElement.hidden = true;
      row.appendChild(titleElement);

      if (this.showVariants && item.product.variants.length > 1) {
        const select = document.createElement('select');
        select.className = 'impressiko-fbt__variant-select';
        select.setAttribute('aria-label', `Choose a variant for ${item.product.title}`);
        item.product.variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = `${variant.title}${variant.available ? '' : ` — ${this.dataset.soldOutLabel}`}`;
          option.disabled = !variant.available;
          option.selected = variant.id === item.variantId;
          select.appendChild(option);
        });
        select.addEventListener('change', () => {
          item.variantId = Number(select.value);
          this.update();
        });
        row.appendChild(select);
      } else {
        const spacer = document.createElement('span');
        spacer.setAttribute('aria-hidden', 'true');
        row.appendChild(spacer);
      }

      const price = document.createElement('div');
      price.className = 'impressiko-fbt__row-price';
      price.innerHTML = '<strong data-fbt-row-price></strong><s data-fbt-row-compare hidden></s>';
      row.appendChild(price);

      return row;
    }

    getVariant(item) {
      return (
        item.product.variants.find((variant) => variant.id === item.variantId) ||
        item.product.variants[0]
      );
    }

    update() {
      let total = 0;
      let compareTotal = 0;
      let selectedCount = 0;

      this.state.forEach((item, index) => {
        const variant = this.getVariant(item);
        const row = this.querySelector(`[data-fbt-row-index="${index}"]`);
        const visual = this.querySelector(`[data-fbt-visual-index="${index}"]`);
        const checkbox = row.querySelector('.impressiko-fbt__checkbox');

        if (!variant?.available) {
          item.selected = false;
          checkbox.checked = false;
          checkbox.disabled = true;
        } else {
          checkbox.disabled = false;
        }

        const price = variant?.price || 0;
        const comparePrice = variant?.compareAtPrice > price ? variant.compareAtPrice : 0;
        row.querySelector('[data-fbt-row-price]').textContent = this.formatMoney(price);

        const rowCompare = row.querySelector('[data-fbt-row-compare]');
        rowCompare.hidden = !(this.showCompare && comparePrice);
        rowCompare.textContent = comparePrice ? this.formatMoney(comparePrice) : '';

        visual.dataset.unselected = item.selected ? 'false' : 'true';
        this.setVisualImage(visual, variant?.image || item.product.image, item.product.title);

        if (item.selected && variant?.available) {
          selectedCount += 1;
          total += price;
          compareTotal += comparePrice || price;
        }
      });

      this.totalElement.textContent = this.formatMoney(total);
      const showBundleCompare = this.showCompare && compareTotal > total;
      this.compareElement.hidden = !showBundleCompare;
      this.compareElement.textContent = showBundleCompare ? this.formatMoney(compareTotal) : '';
      this.buttonElement.disabled = selectedCount === 0 || this.buttonElement.dataset.loading === 'true';
      preservedSelections.set(this.id, this.state.map((item) => ({
        product: { id: item.product.id },
        selected: item.selected,
        variantId: item.variantId,
      })));
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney && this.dataset.moneyFormat) {
        try {
          return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
        } catch (error) {
          // Fall through to the browser currency formatter.
        }
      }
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    }

    selectedItems() {
      return this.state
        .filter((item) => item.selected && this.getVariant(item)?.available)
        .map((item) => ({ id: this.getVariant(item).id, quantity: 1 }));
    }

    async addSelectedToCart() {
      const items = this.selectedItems();
      if (!items.length || this.buttonElement.dataset.loading === 'true') return;

      this.setMessage('', false);
      this.buttonElement.dataset.loading = 'true';
      this.buttonElement.disabled = true;

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        let sections = ['cart-drawer', 'cart-icon-bubble'];
        const renderer = cartDrawer || cartNotification;

        if (renderer && typeof renderer.getSectionsToRender === 'function') {
          sections = renderer
            .getSectionsToRender()
            .map((section) => section.section || section.id)
            .filter(Boolean)
            .slice(0, 5);
        }

        sections = [...new Set(sections)].slice(0, 5);

        const response = await fetch(`${this.routesRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add the selected products.');

        if (renderer && typeof renderer.renderContents === 'function') {
          renderer.renderContents(data);
        } else if (data.sections) {
          this.renderReturnedSections(data.sections);
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { source: 'impressiko-fbt', response: data },
          })
        );

        if (cartDrawer && typeof cartDrawer.open === 'function') {
          window.setTimeout(() => cartDrawer.open(), 80);
        } else if (cartNotification && typeof cartNotification.open === 'function') {
          window.setTimeout(() => cartNotification.open(), 80);
        }

        this.setMessage(this.dataset.successMessage || 'Selected products added to your cart.', false);
        this.buttonElement.classList.add('is-success');
        window.setTimeout(() => this.buttonElement.classList.remove('is-success'), 1800);
      } catch (error) {
        this.setMessage(error.message || this.dataset.errorMessage || 'Something went wrong. Please try again.', true);
      } finally {
        this.buttonElement.dataset.loading = 'false';
        this.update();
      }
    }

    renderReturnedSections(sections) {
      Object.entries(sections).forEach(([sectionId, html]) => {
        if (!html) return;
        const target =
          document.getElementById(`shopify-section-${sectionId}`) ||
          document.getElementById(sectionId) ||
          document.querySelector(`[data-section-id="${sectionId}"]`);
        if (!target) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement =
          parsed.getElementById(`shopify-section-${sectionId}`) || parsed.body.firstElementChild;
        if (replacement) target.innerHTML = replacement.innerHTML;
      });
    }

    setMessage(message, isError) {
      this.messageElement.textContent = message;
      this.messageElement.dataset.error = isError ? 'true' : 'false';
    }
  }

  customElements.define('impressiko-fbt', ImpressikoFBT);
})();
